import {
  DocumentCategory,
  DocumentLinkedEventKind,
  DocumentSubCategory,
} from './document-category.enum';

const subCategoryLinkedEventKind: Record<DocumentSubCategory, DocumentLinkedEventKind> = {
  [DocumentSubCategory.SHARE_CLASSES_BOARD_RESOLUTION]: DocumentLinkedEventKind.SHARE_CLASS_RECORD,
  [DocumentSubCategory.POOLS_BOARD_RESOLUTION]: DocumentLinkedEventKind.POOL_RECORD,
  [DocumentSubCategory.PLANS_BOARD_RESOLUTION]: DocumentLinkedEventKind.PLAN_RECORD,
  [DocumentSubCategory.PLANS_PLAN_AGREEMENT]: DocumentLinkedEventKind.PLAN_RECORD,
  [DocumentSubCategory.FUNDRAISING_ROUND_SUBSCRIPTION_AGREEMENT]:
    DocumentLinkedEventKind.INVESTMENT_TRANSACTION,
  [DocumentSubCategory.FUNDRAISING_ROUND_SHAREHOLDER_AGREEMENT]:
    DocumentLinkedEventKind.INVESTMENT_TRANSACTION,
  [DocumentSubCategory.SHARE_ISSUANCE_BOARD_RESOLUTION]:
    DocumentLinkedEventKind.SHARE_ISSUANCE_EVENT_GENERAL,
  [DocumentSubCategory.SHARE_ISSUANCE_SHARE_CERTIFICATE]:
    DocumentLinkedEventKind.SHARE_ISSUANCE_TRANSACTION_INDIVIDUAL,
  [DocumentSubCategory.BUYBACK_BOARD_RESOLUTION]: DocumentLinkedEventKind.BUYBACK_EVENT_GENERAL,
  [DocumentSubCategory.BUYBACK_SHARE_REPURCHASE_AGREEMENT]:
    DocumentLinkedEventKind.BUYBACK_TRANSACTION_INDIVIDUAL,
  [DocumentSubCategory.CLASS_CONVERSION_BOARD_RESOLUTION]:
    DocumentLinkedEventKind.CLASS_CONVERSION_EVENT_GENERAL,
  [DocumentSubCategory.SECONDARIES_BOARD_RESOLUTION]:
    DocumentLinkedEventKind.SECONDARIES_EVENT_GENERAL,
  [DocumentSubCategory.SECONDARIES_PURCHASE_AND_SELL_AGREEMENT]:
    DocumentLinkedEventKind.SECONDARIES_TRANSACTION_INDIVIDUAL,
  [DocumentSubCategory.VALUATION_EVENT_BOARD_RESOLUTION]: DocumentLinkedEventKind.VALUATION_EVENT,
  [DocumentSubCategory.SAFE_SAFE_AGREEMENT]: DocumentLinkedEventKind.SAFE_RECORD,
  [DocumentSubCategory.SAFE_SAFE_CONVERSION_NOTICE]: DocumentLinkedEventKind.SAFE_CONVERSION_RECORD,
  [DocumentSubCategory.GRANT_AGREEMENT]: DocumentLinkedEventKind.GRANT_TRANSACTION,
  [DocumentSubCategory.GRANT_EXERCISE_NOTICE]: DocumentLinkedEventKind.EXERCISE_TRANSACTION,
  [DocumentSubCategory.GRANT_PHANTOM_PAYOUT]: DocumentLinkedEventKind.PHANTOM_PAYOUT_TRANSACTION,
  [DocumentSubCategory.GRANT_HURDLE_APPROVAL]: DocumentLinkedEventKind.HURDLE_APPROVAL_TRANSACTION,
  [DocumentSubCategory.OTHER_OTHER]: DocumentLinkedEventKind.NONE,
};

export const DOCUMENT_CATEGORIES_SORTED: DocumentCategory[] = [
  DocumentCategory.BUYBACK,
  DocumentCategory.CLASS_CONVERSION,
  DocumentCategory.FUNDRAISING_ROUND,
  DocumentCategory.GRANT,
  DocumentCategory.PLANS,
  DocumentCategory.POOLS,
  DocumentCategory.SAFE,
  DocumentCategory.SECONDARIES,
  DocumentCategory.SHARE_CLASSES,
  DocumentCategory.SHARE_ISSUANCE,
  DocumentCategory.VALUATION_EVENT,
  DocumentCategory.OTHER,
];

export const DOCUMENT_SUB_CATEGORIES_BY_CATEGORY: Record<DocumentCategory, DocumentSubCategory[]> =
  {
    [DocumentCategory.BUYBACK]: [
      DocumentSubCategory.BUYBACK_BOARD_RESOLUTION,
      DocumentSubCategory.BUYBACK_SHARE_REPURCHASE_AGREEMENT,
    ],
    [DocumentCategory.CLASS_CONVERSION]: [DocumentSubCategory.CLASS_CONVERSION_BOARD_RESOLUTION],
    [DocumentCategory.FUNDRAISING_ROUND]: [
      DocumentSubCategory.FUNDRAISING_ROUND_SHAREHOLDER_AGREEMENT,
      DocumentSubCategory.FUNDRAISING_ROUND_SUBSCRIPTION_AGREEMENT,
    ],
    [DocumentCategory.GRANT]: [
      DocumentSubCategory.GRANT_EXERCISE_NOTICE,
      DocumentSubCategory.GRANT_AGREEMENT,
      DocumentSubCategory.GRANT_HURDLE_APPROVAL,
      DocumentSubCategory.GRANT_PHANTOM_PAYOUT,
    ],
    [DocumentCategory.OTHER]: [DocumentSubCategory.OTHER_OTHER],
    [DocumentCategory.PLANS]: [
      DocumentSubCategory.PLANS_BOARD_RESOLUTION,
      DocumentSubCategory.PLANS_PLAN_AGREEMENT,
    ],
    [DocumentCategory.POOLS]: [DocumentSubCategory.POOLS_BOARD_RESOLUTION],
    [DocumentCategory.SAFE]: [
      DocumentSubCategory.SAFE_SAFE_AGREEMENT,
      DocumentSubCategory.SAFE_SAFE_CONVERSION_NOTICE,
    ],
    [DocumentCategory.SECONDARIES]: [
      DocumentSubCategory.SECONDARIES_BOARD_RESOLUTION,
      DocumentSubCategory.SECONDARIES_PURCHASE_AND_SELL_AGREEMENT,
    ],
    [DocumentCategory.SHARE_CLASSES]: [DocumentSubCategory.SHARE_CLASSES_BOARD_RESOLUTION],
    [DocumentCategory.SHARE_ISSUANCE]: [
      DocumentSubCategory.SHARE_ISSUANCE_BOARD_RESOLUTION,
      DocumentSubCategory.SHARE_ISSUANCE_SHARE_CERTIFICATE,
    ],
    [DocumentCategory.VALUATION_EVENT]: [DocumentSubCategory.VALUATION_EVENT_BOARD_RESOLUTION],
  };

export const isDocumentSubCategoryValidForCategory = (
  category: DocumentCategory,
  subCategory: DocumentSubCategory,
): boolean => DOCUMENT_SUB_CATEGORIES_BY_CATEGORY[category]?.includes(subCategory) ?? false;

export const getDocumentLinkedEventKind = (
  subCategory: DocumentSubCategory,
): DocumentLinkedEventKind => subCategoryLinkedEventKind[subCategory];

const documentSubCategoryValues = new Set<string>(Object.values(DocumentSubCategory));

export const isDocumentSubCategory = (value: string): value is DocumentSubCategory =>
  documentSubCategoryValues.has(value);

export const getDocumentLinkedEventKindFromSubCategory = (
  subCategory: string,
): DocumentLinkedEventKind => {
  if (!isDocumentSubCategory(subCategory)) {
    return DocumentLinkedEventKind.NONE;
  }
  return getDocumentLinkedEventKind(subCategory);
};

export const documentSubCategoryRequiresLinkedEvent = (subCategory: DocumentSubCategory): boolean =>
  getDocumentLinkedEventKind(subCategory) !== DocumentLinkedEventKind.NONE;

export const documentSubCategoryRequiresLinkedEventFromSubCategory = (
  subCategory: string,
): boolean =>
  getDocumentLinkedEventKindFromSubCategory(subCategory) !== DocumentLinkedEventKind.NONE;
